import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
	Component,
	input,
	output,
	ViewChild,
	inject,
	signal,
	computed,
	effect,
	OnDestroy,
	OnInit,
	PLATFORM_ID,
	Inject
} from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
	StripeService,
	StripeElementsDirective,
	StripePaymentElementComponent,
	StripeAddressComponent
} from 'ngx-stripe';
import {
	StripeAddressElementChangeEvent,
	StripeAddressElementOptions,
	StripeElements,
	StripeElementsOptions
} from '@stripe/stripe-js';
import { Subscription } from 'rxjs';
import { StripeCheckoutService } from '@sp-client/app/services/stripe-checkout.service';
import { PlanService } from '@sp-client/app/services/plan.service';
import { UserService } from '@sp-client/app/services/user.service';
import { ApiService } from '@sp-client/app/services/api.service';
import { LogService } from '@sp-client/app/services/log.service';
import { TaxService } from '@sp-client/app/services/tax.service';
import { StoreService } from '@sp-client/app/services/store.service';
import { ProperTitleCasePipe } from '@sp-client/app/pipes/proper-title-case.pipe';
import { ButtonComponent } from '../button/button.component';
import { ImageComponent } from '../image/image.component';
import { PillComponent } from '../pill/pill.component';
import { ProgressDotsComponent } from '../progress-dots/progress-dots.component';

// Receipt data structure
interface ReceiptData {
	orderNumber?: string | null;
	name: string;
	billingAddress: {
		line1?: string;
		line2?: string;
		city?: string;
		state?: string;
		postal_code?: string;
		country?: string;
	};
	cardLast4?: string;
	subtotal: number;
	tax: number;
	total: number;
	currency: string;
}

@Component({
	selector: 'sp-store-checkout',
	imports: [
		CommonModule,
		ReactiveFormsModule,
		StripeElementsDirective,
		StripePaymentElementComponent,
		StripeAddressComponent,
		ButtonComponent,
		ImageComponent,
		PillComponent,
		ProperTitleCasePipe,
		ProgressDotsComponent
	],
	templateUrl: './store-checkout.component.html',
	styleUrl: './store-checkout.component.scss'
})
export class StoreCheckoutComponent implements OnInit, OnDestroy {
	@ViewChild(StripePaymentElementComponent) paymentElement!: StripePaymentElementComponent;

	readonly amount = input<number>(0);
	readonly currency = input<string>('usd');
	readonly description = input<string>('');
	readonly disabled = input<boolean>(false);
	readonly loading = input<boolean>(false);
	readonly initialPaymentType = input<'one-time' | 'subscription' | null>(null);

	readonly stripeService = inject(StripeService);
	private readonly stripeCheckoutService = inject(StripeCheckoutService);
	private readonly planService = inject(PlanService);
	private readonly userService = inject(UserService);
	private readonly apiService = inject(ApiService);
	private readonly log = inject(LogService);
	private readonly formBuilder = inject(FormBuilder);
	private readonly taxService = inject(TaxService);
	private readonly storeService = inject(StoreService);

	checkoutForm: FormGroup;
	private paymentTypeSubscription?: Subscription;
	private hasInitialized = signal<boolean>(false);
	private hasTerminalSubscriptionConflict = signal<boolean>(false);

	checkoutInitiated = output<void>();
	checkoutCompleted = output<unknown>();
	checkoutError = output<Error>();
	goBack = output<void>();
	checkoutSucceeded = output<ReceiptData>();

	selectedPlan = computed(() => this.planService.selectedPlanSignal());
	countryName = computed(() => this.selectedPlan()?.countryName || null);
	countryNameImage = computed(() => {
		const name = this.selectedPlan()?.countryName;
		return name ? name.replace(/\s+/g, '-') : null;
	});

	// Product selection from service (returns RadioButtonOption | null)
	selectedProduct = computed(() => this.storeService.getSelectedProductSignal()());
	productOptions = computed(() => this.storeService.getProductOptions());

	// Get pricing signal from service
	pricing = computed(() => this.storeService.getPricingSignal()());

	// Computed signal for selected product price
	selectedProductPrice = computed(() => {
		const product = this.selectedProduct();
		const pricing = this.pricing();

		if (!product || !pricing) {
			return 0;
		}

		// Map product value to pricing field
		// 'yearly' -> oneTime, 'monthly' -> subscription
		if (product.value === 'yearly') {
			return pricing.oneTime;
		} else if (product.value === 'monthly') {
			return pricing.subscription;
		}

		return 0;
	});

	constructor(@Inject(PLATFORM_ID) private platformId: object) {
		this.checkoutForm = this.formBuilder.group({
			// Wrap Validators.required to satisfy @typescript-eslint/unbound-method
			paymentType: ['one-time', [(control: AbstractControl) => Validators.required(control)]]
		});

		// Sync form with selectedPaymentType signal
		this.paymentTypeSubscription = this.checkoutForm.get('paymentType')?.valueChanges.subscribe((value: string) => {
			if (value === 'one-time' || value === 'subscription') {
				if (value !== this.selectedPaymentType()) {
					this.selectedPaymentType.set(value);
				}
			}
		});

		// Keep checkout payment type in sync with selected product.
		// This must react even after a client secret already exists, otherwise the component
		// can stay on an old one-time secret when user switches to monthly/subscription.
		effect(() => {
			const plan = this.selectedPlan();
			const initialType = this.initialPaymentType();
			const currentType = this.selectedPaymentType();
			const secret = this.clientSecret();
			const terminalConflict = this.hasTerminalSubscriptionConflict();
			if (!plan?.id || !initialType) {
				return;
			}
			if (terminalConflict) {
				return;
			}

			// If the selected product changed checkout mode, force refresh for the new mode.
			if (initialType !== currentType) {
				void this.selectPaymentType(initialType);
				return;
			}

			// If mode is aligned but secret is missing, initialize it.
			if (!secret) {
				void this.selectPaymentType(initialType);
			}
		});
	}

	ngOnInit(): void {
		// Initialize payment type if provided
		const initialType = this.initialPaymentType();
		if (initialType) {
			this.selectedPaymentType.set(initialType);
			this.checkoutForm.patchValue({ paymentType: initialType }, { emitEvent: false });
			// Auto-initialize payment by selecting the payment type
			void this.selectPaymentType(initialType);
		}

		// Address changes are handled via `(change)` binding on <ngx-stripe-address>.

		// Check for Stripe redirect parameters in URL
		// When Stripe redirects for 3D Secure, it returns with payment_intent and redirect_status in the URL
		// Only run in browser environment
		if (isPlatformBrowser(this.platformId)) {
			void this.handleStripeRedirect();
		}
	}

	/**
	 * Handles Stripe Address Element changes to keep billing address and tax in sync.
	 */
	onBillingAddressChange(event: StripeAddressElementChangeEvent): void {
		const addr = event?.value?.address;
		if (!addr) {
			// Clear address if no address data
			this.billingAddress.set({});
			return;
		}

		const billing = {
			state: addr.state,
			postal_code: addr.postal_code,
			country: addr.country,
			city: addr.city,
			line1: addr.line1,
			line2: addr.line2
		};

		// Only update if address actually changed to prevent unnecessary recalculations
		const currentAddress = this.billingAddress();
		if (
			currentAddress?.country !== billing.country ||
			currentAddress?.state !== billing.state ||
			currentAddress?.postal_code !== billing.postal_code ||
			currentAddress?.city !== billing.city
		) {
			this.billingAddress.set(billing);
			this.log.debug('Billing address updated for tax calculation', billing);
		}
	}

	ngOnDestroy(): void {
		this.paymentTypeSubscription?.unsubscribe();
		// Clear any pending tax calculation timer
		if (this.taxCalculationTimer) {
			clearTimeout(this.taxCalculationTimer);
			this.taxCalculationTimer = null;
		}
	}

	/**
	 * Handles Stripe redirect after 3D Secure authentication
	 * Checks URL parameters for payment_intent and redirect_status
	 */
	private async handleStripeRedirect(): Promise<void> {
		// Only run in browser environment
		if (!isPlatformBrowser(this.platformId)) {
			return;
		}

		const urlParams = new URLSearchParams(window.location.search);
		const paymentIntentId = urlParams.get('payment_intent');
		const redirectStatus = urlParams.get('redirect_status');

		if (paymentIntentId && redirectStatus) {
			this.log.debug('Stripe redirect detected', {
				paymentIntentId,
				redirectStatus
			});

			// If payment succeeded, complete the unlock flow
			if (redirectStatus === 'succeeded') {
				this.log.info('Payment succeeded after redirect, unlocking plan', {
					paymentIntentId
				});

				// Set processing state
				this.isProcessingPayment.set(true);
				this.errorMessage.set(null);

				try {
					const plan = this.selectedPlan();
					if (!plan?.id) {
						this.log.warn('No plan available to unlock after redirect');
						// Clean up URL parameters
						this.cleanupUrl();
						return;
					}

					// Verify payment intent and unlock plan
					const verificationResult = await this.apiService.verifyPaymentIntent(paymentIntentId, plan.id);

					this.log.debug('Payment verification result after redirect', {
						success: verificationResult.success,
						planId: verificationResult.plan?.id,
						purchaseLock: verificationResult.plan?.purchaseLock
					});

					if (verificationResult.success && verificationResult.plan) {
						const unlockedPlan = verificationResult.plan;

						if (unlockedPlan.purchaseLock === false) {
							// Update local selected plan signal with unlocked plan
							this.planService.selectedPlanSignal.set(unlockedPlan);
							this.planService.setPlanIsLocked(false);

							this.log.debug('Plan unlocked after redirect', {
								planId: unlockedPlan.id,
								purchaseLock: unlockedPlan.purchaseLock
							});

							// Refresh full plan data
							setTimeout(() => {
								void (async () => {
									try {
										await this.planService.getFullPlanData(plan.id);
										const refreshedPlan = this.planService.selectedPlanSignal();
										if (refreshedPlan && refreshedPlan.purchaseLock === false) {
											this.log.debug('Plan refreshed and confirmed unlocked after redirect', {
												planId: refreshedPlan.id,
												purchaseLock: refreshedPlan.purchaseLock
											});
										}
									} catch (refreshError) {
										this.log.error('Error refreshing plan after redirect:', refreshError);
									}
								})();
							}, 1000);

							// Ensure receipt data is set before showing success
							// Use orderNumber from verification result or paymentIntentId as fallback
							const orderNumber: string | null = verificationResult.orderNumber || paymentIntentId || null;
							this.ensureReceiptData(orderNumber);

							// Set success state and emit success event
							this.paymentSucceeded.set(true);

							// Ensure receipt data is set before emitting
							if (!this.receiptData()) {
								this.ensureReceiptData(orderNumber);
							}

							// Emit receipt data to show receipt page
							const receipt = this.receiptData();
							if (receipt) {
								// Emit checkoutSucceeded to show receipt page
								this.checkoutSucceeded.emit(receipt);
							} else {
								// Fallback: if receipt data still isn't available, create basic receipt
								this.log.warn('Receipt data not available in redirect handler, using fallback');
								this.ensureReceiptData(orderNumber);
								const fallbackReceipt = this.receiptData();
								if (fallbackReceipt) {
									this.checkoutSucceeded.emit(fallbackReceipt);
								}
							}
							// Don't emit checkoutCompleted here - checkoutSucceeded handles showing receipt
							// this.checkoutCompleted.emit({ paymentIntent: { id: paymentIntentId, status: 'succeeded' } });
						}
					}

					// Clean up URL parameters
					this.cleanupUrl();
				} catch (error) {
					this.log.error('Error handling Stripe redirect:', error);
					const errorMsg = error instanceof Error ? error.message : 'Error processing payment after redirect.';
					this.errorMessage.set(errorMsg);
					this.checkoutError.emit(error instanceof Error ? error : new Error(errorMsg));
				} finally {
					this.isProcessingPayment.set(false);
				}
			} else if (redirectStatus === 'failed') {
				// Payment failed after redirect
				this.log.warn('Payment failed after redirect', { paymentIntentId });
				const errorMsg = 'Payment failed. Please try again.';
				this.errorMessage.set(errorMsg);
				this.paymentErrorMessage.set(errorMsg);
				this.checkoutError.emit(new Error('Payment failed after redirect'));
				this.cleanupUrl();
			}
		}
	}

	/**
	 * Removes Stripe redirect parameters from URL
	 */
	private cleanupUrl(): void {
		// Only run in browser environment
		if (!isPlatformBrowser(this.platformId)) {
			return;
		}

		const url = new URL(window.location.href);
		url.searchParams.delete('payment_intent');
		url.searchParams.delete('redirect_status');
		url.searchParams.delete('payment_intent_client_secret');
		// Update URL without page reload
		window.history.replaceState({}, '', url.toString());
	}

	// Payment type selection
	selectedPaymentType = signal<'one-time' | 'subscription'>('one-time');

	// Client secret state
	clientSecret = signal<string | null>(null);
	isLoadingClientSecret = signal<boolean>(false);
	errorMessage = signal<string | null>(null);
	isProcessingPayment = signal<boolean>(false);
	paymentErrorMessage = signal<string | null>(null);
	paymentSucceeded = signal<boolean>(false);
	receiptData = signal<ReceiptData | null>(null);

	// User data
	user = computed(() => this.userService.userSignal());

	// Full name from user data
	fullName = computed(() => {
		const user = this.user();
		if (user?.firstName && user?.lastName) {
			return `${user.firstName} ${user.lastName}`;
		}
		if (user?.firstName) {
			return user.firstName;
		}
		if (user?.lastName) {
			return user.lastName;
		}
		return null;
	});

	// Computed signals for button states
	isOneTimeSelected = computed(() => this.selectedPaymentType() === 'one-time');
	isSubscriptionSelected = computed(() => this.selectedPaymentType() === 'subscription');

	// Billing address signal for tax calculation
	billingAddress = signal<{
		state?: string;
		postal_code?: string;
		country?: string;
		city?: string;
		line1?: string;
		line2?: string;
	}>({});

	// Computed signal for display amount based on selected product
	displayAmount = computed(() => {
		// Use selected product price if available
		const productPrice = this.selectedProductPrice();
		if (productPrice > 0) {
			return productPrice;
		}

		// Fallback to amount input (for backward compatibility)
		return this.amount();
	});

	// Price breakdown computed signals
	subtotal = computed(() => this.displayAmount());

	// Tax signal - updated asynchronously when billing address or subtotal changes
	taxSignal = signal<number>(0);

	// Loading state for tax calculation
	isCalculatingTax = signal<boolean>(false);

	// Tax computed from signal
	tax = computed(() => this.taxSignal());

	total = computed(() => this.subtotal() + this.tax());

	// Debounce timer for tax calculation to avoid too many API calls
	private taxCalculationTimer: ReturnType<typeof setTimeout> | null = null;
	private readonly TAX_CALCULATION_DEBOUNCE_MS = 500; // 500ms debounce

	// Effect to update tax when billing address or subtotal changes
	private taxCalculationEffect = effect(() => {
		const subtotalValue = this.subtotal();
		const address = this.billingAddress();

		// Clear any pending tax calculation
		if (this.taxCalculationTimer) {
			clearTimeout(this.taxCalculationTimer);
		}

		// Debounce tax calculation to avoid excessive API calls
		// This is especially important since Stripe Address Element change event
		// can fire multiple times as user types
		this.taxCalculationTimer = setTimeout(() => {
			// Use void to explicitly mark async operation
			// This ensures the effect properly tracks dependencies
			void this.updateTaxCalculation(subtotalValue, address);
			this.taxCalculationTimer = null;
		}, this.TAX_CALCULATION_DEBOUNCE_MS);
	});

	/**
	 * Updates tax calculation asynchronously
	 * Separated from effect to ensure proper dependency tracking
	 */
	private async updateTaxCalculation(
		subtotalValue: number,
		address: {
			state?: string;
			postal_code?: string;
			country?: string;
			city?: string;
			line1?: string;
		}
	): Promise<void> {
		// Only calculate if we have an address
		if (address && (address.country || address.state || address.postal_code)) {
			// Set loading state
			this.isCalculatingTax.set(true);

			try {
				this.log.debug('Calculating tax for address:', address);
				const taxAmount = await this.taxService.calculateTax(subtotalValue, address);

				// Only update if address hasn't changed (prevent race conditions)
				const currentAddress = this.billingAddress();
				const addressKey = `${address.country}-${address.state}-${address.postal_code}-${address.city || ''}`;
				const currentKey = `${currentAddress?.country || ''}-${currentAddress?.state || ''}-${currentAddress?.postal_code || ''}-${currentAddress?.city || ''}`;

				if (addressKey === currentKey) {
					this.taxSignal.set(taxAmount);
					this.log.debug('Tax updated:', { taxAmount, address });
				} else {
					this.log.debug('Tax calculation skipped - address changed during calculation', {
						calculatedFor: addressKey,
						current: currentKey
					});
				}
			} catch (error) {
				this.log.error('Error calculating tax:', error);
				// Set to 0 on error only if address still matches
				const currentAddress = this.billingAddress();
				const addressKey = `${address.country}-${address.state}-${address.postal_code}-${address.city || ''}`;
				const currentKey = `${currentAddress?.country || ''}-${currentAddress?.state || ''}-${currentAddress?.postal_code || ''}-${currentAddress?.city || ''}`;

				if (addressKey === currentKey) {
					this.taxSignal.set(0);
				}
			} finally {
				// Clear loading state
				this.isCalculatingTax.set(false);
			}
		} else {
			// No address, set tax to 0
			this.taxSignal.set(0);
			this.isCalculatingTax.set(false);
		}
	}

	// Computed signal for payment type description
	paymentTypeDescription = computed(() => {
		if (this.selectedPaymentType() === 'one-time') {
			return '12 month access to Scoutly Plans service';
		}
		const product = this.selectedProduct();
		const pricing = this.pricing();
		if (product?.value === 'yearly') {
			return `Yearly payment of $${pricing?.oneTime ?? 299}`;
		}
		return `Monthly payment of $${pricing?.subscription ?? 29}`;
	});

	// Elements options with client secret
	elementsOptions = computed<StripeElementsOptions>(() => {
		const options: StripeElementsOptions = {
			locale: 'en',
			appearance: {
				theme: 'stripe' as const,
				variables: {
					colorText: '#ffffff',
					colorBackground: '#184043',
					colorPrimary: '#ffffff',
					iconColor: '#ffffff',
					fontFamily: 'Poppins, system-ui, sans-serif',
					colorTextSecondary: '#c7d2d3',
					colorTextPlaceholder: '#c7d2d3',
					accessibleColorOnColorBackground: '#184043'
				},
				rules: {
					'.Input': {
						border: '1px solid #c7d2d3'
					},
					'.AccordionItem': {
						border: '0px solid #c7d2d3',
						padding: '0'
					}
				}
			}
		};

		const secret = this.clientSecret();
		if (secret) {
			options.clientSecret = secret;
		}

		return options;
	});

	billingAddressOptions = computed<StripeAddressElementOptions>(() => {
		// Create a stable options object - mode cannot be changed after element creation
		const options: StripeAddressElementOptions = {
			mode: 'billing'
		};

		const name = this.fullName();
		if (name) {
			options.defaultValues = {
				name: name
			};
		}

		// Return a new object reference only when name changes, not on every computation
		// This prevents Stripe from trying to update the mode
		return { ...options };
	});

	/**
	 * Selects payment type and fetches client secret
	 */
	async selectPaymentType(type: 'one-time' | 'subscription'): Promise<void> {
		// Prevent multiple simultaneous requests
		if (this.isLoadingClientSecret()) {
			return;
		}
		if (type === 'subscription' && this.hasTerminalSubscriptionConflict()) {
			return;
		}

		// If we already have a client secret for this payment type, don't fetch again
		const currentType = this.selectedPaymentType();
		const currentSecret = this.clientSecret();
		if (currentSecret && currentType === type) {
			return;
		}

		// Check if plan is available before proceeding
		const plan = this.selectedPlan();
		if (!plan?.id) {
			this.errorMessage.set('Please select a plan to continue.');
			return;
		}
		if (
			type === 'subscription' &&
			(plan.entitlement?.stripeStatus === 'active' || plan.entitlement?.stripeStatus === 'trialing')
		) {
			this.errorMessage.set('This plan already has an active subscription. Use Manage Billing to make changes.');
			return;
		}

		// Only update payment type if it's different
		if (currentType !== type) {
			this.selectedPaymentType.set(type);
			// Use emitEvent: false to prevent triggering valueChanges subscription
			// since we're already manually setting the signal above
			this.checkoutForm.patchValue({ paymentType: type }, { emitEvent: false });
			// Reset terminal conflict state when user changes checkout mode.
			this.hasTerminalSubscriptionConflict.set(false);
		}

		this.errorMessage.set(null);
		this.isLoadingClientSecret.set(true);

		// Only clear client secret if switching payment types
		if (currentType !== type) {
			this.clientSecret.set(null);
		}

		try {
			const planId = this.stripeCheckoutService.validatePlanId(plan);
			const selectedProduct = this.selectedProduct();
			const billingInterval: 'month' | 'year' = selectedProduct?.value === 'yearly' ? 'year' : 'month';

			const secret = await this.stripeCheckoutService.getClientSecret(type, planId, billingInterval);
			this.clientSecret.set(secret);
			this.hasInitialized.set(true);
		} catch (error: unknown) {
			const errorMsg = error instanceof Error ? error.message : 'Failed to initialize payment. Please try again.';
			const isActiveSubscriptionConflict =
				typeof errorMsg === 'string' && errorMsg.toLowerCase().includes('active subscription already exists');
			if (isActiveSubscriptionConflict) {
				this.hasTerminalSubscriptionConflict.set(true);
			}
			this.errorMessage.set(errorMsg);
			this.checkoutError.emit(error instanceof Error ? error : new Error(errorMsg));
		} finally {
			this.isLoadingClientSecret.set(false);
		}
	}

	/**
	 * Handles checkout/payment confirmation
	 */
	async handleCheckout(): Promise<void> {
		if (this.disabled() || this.loading() || this.isProcessingPayment() || !this.clientSecret()) {
			this.log.warn('Checkout not possible - disabled, loading, or no client secret', {
				disabled: this.disabled(),
				loading: this.loading(),
				isProcessingPayment: this.isProcessingPayment(),
				clientSecret: this.clientSecret()
			});
			return;
		}

		if (!this.checkoutForm.valid) {
			this.errorMessage.set('Please complete all required fields.');
			return;
		}

		this.checkoutInitiated.emit();
		this.isProcessingPayment.set(true);
		this.errorMessage.set(null);
		this.paymentErrorMessage.set(null); // Clear previous payment errors
		this.log.debug('Payment processing started - isProcessingPayment set to true');

		try {
			// Get elements from StripePaymentElementComponent
			// The paymentElement component exposes the elements instance
			if (!this.paymentElement?.elements) {
				throw new Error('Payment form is not ready. Please wait a moment and try again.');
			}

			const elements = this.paymentElement.elements;

			const secret = this.clientSecret();

			if (!secret) {
				throw new Error('Payment session expired. Please select a payment type again.');
			}

			// Extract user information
			const user = this.user();
			const userEmail = user?.emailAddress || null;
			const userName = this.fullName();

			// Confirm payment with Stripe, passing user information
			const result = await this.stripeCheckoutService.confirmPaymentWithStripe(
				this.stripeService,
				elements,
				secret,
				userName,
				userEmail,
				this.billingAddress()
			);

			// Extract payment intent ID and status from result
			const plan = this.selectedPlan();
			if (!plan?.id) {
				this.log.warn('No plan available to unlock after payment');
				const successResult = this.stripeCheckoutService.handlePaymentSuccess(result);
				this.checkoutCompleted.emit(successResult);
				// Set processing to false since we're done
				this.isProcessingPayment.set(false);
				return; // Exit early to skip finally block
			}

			// Extract payment intent ID and status from result
			let paymentIntentId: string | undefined;
			let paymentIntentStatus: string | undefined;

			// Log the full result structure to debug
			this.log.debug('Full payment confirmation result', {
				result,
				resultType: typeof result,
				resultKeys: result && typeof result === 'object' ? Object.keys(result as Record<string, unknown>) : []
			});

			// Try to extract from result object first (most reliable)
			if (result && typeof result === 'object') {
				const resultObj = result as unknown as Record<string, unknown>;

				// Check for paymentIntent in result
				const paymentIntent = resultObj['paymentIntent'] as Record<string, unknown> | undefined;
				if (paymentIntent) {
					paymentIntentId = paymentIntent['id'] as string | undefined;
					paymentIntentStatus = paymentIntent['status'] as string | undefined;
					this.log.debug('Extracted from paymentIntent object', {
						id: paymentIntentId,
						status: paymentIntentStatus,
						paymentIntentKeys: Object.keys(paymentIntent)
					});
				}

				// Also check if result itself has id and status (some Stripe responses are structured this way)
				if (!paymentIntentId && resultObj['id']) {
					paymentIntentId = resultObj['id'] as string | undefined;
					paymentIntentStatus = resultObj['status'] as string | undefined;
					this.log.debug('Extracted from result object directly', {
						id: paymentIntentId,
						status: paymentIntentStatus
					});
				}
			}

			// Fallback: extract from client secret (format: pi_xxx_secret_yyy)
			if (!paymentIntentId) {
				const clientSecretValue = this.clientSecret();
				if (clientSecretValue) {
					const secretParts = clientSecretValue.split('_secret_');
					if (secretParts.length > 0 && secretParts[0].startsWith('pi_')) {
						paymentIntentId = secretParts[0];
						this.log.debug('Extracted payment intent ID from client secret', { paymentIntentId });
					}
				}
			}

			this.log.debug('Payment confirmation result summary', {
				paymentIntentId,
				paymentIntentStatus,
				hasResult: !!result,
				willUnlock: paymentIntentId && paymentIntentStatus === 'succeeded'
			});

			// Verify and unlock if we have a payment intent ID
			// If status is 'succeeded', unlock immediately
			// If status is undefined or not 'succeeded', still try to verify (backend will check actual status)
			if (paymentIntentId) {
				// If status is explicitly 'succeeded', unlock immediately
				if (paymentIntentStatus === 'succeeded') {
					this.log.debug('Payment succeeded (from result), unlocking plan', {
						paymentIntentId,
						planId: plan.id,
						status: paymentIntentStatus
					});
				} else {
					// Status is undefined or not 'succeeded' - still verify on backend
					// Backend will check the actual status in Stripe
					this.log.debug('Payment status unclear from result, verifying on backend', {
						paymentIntentId,
						planId: plan.id,
						statusFromResult: paymentIntentStatus
					});
				}

				try {
					// Call API to verify payment intent and unlock plan on server
					// This sets purchaseLock = false and saves to Firestore via backend unlockPlan -> updatePlan
					// The backend will check the actual payment intent status in Stripe
					const verificationResult = await this.apiService.verifyPaymentIntent(paymentIntentId, plan.id);

					this.log.debug('Payment verification result', {
						success: verificationResult.success,
						planId: verificationResult.plan?.id,
						purchaseLock: verificationResult.plan?.purchaseLock
					});

					if (verificationResult.success && verificationResult.plan) {
						// Update local selected plan signal with unlocked plan (purchaseLock = false)
						const unlockedPlan = verificationResult.plan;

						// Verify the plan is actually unlocked
						if (unlockedPlan.purchaseLock === false) {
							// Set the unlocked plan - the effect in PlanService will automatically update planIsLockedSignal
							this.planService.selectedPlanSignal.set(unlockedPlan);

							this.log.debug('unlockedPlan', JSON.stringify(unlockedPlan));

							// Explicitly ensure the locked signal is set to false
							// The effect should handle this, but we do it explicitly to ensure it updates immediately
							// this.planService.setPlanIsLocked(false);

							this.log.debug('Plan unlocked and local state updated', {
								planId: unlockedPlan.id,
								purchaseLock: unlockedPlan.purchaseLock,
								planIsLocked: this.planService.planIsLockedSignal()
							});

							// Wait a moment for backend to fully save to Firestore, then refresh full plan data
							// This ensures we get the complete plan structure with ordered guides
							// setTimeout(() => {
							// 	void (async () => {
							// 		try {
							// 			await this.planService.getFullPlanData(plan.id);
							// 			// Verify the refreshed plan is still unlocked
							// 			const refreshedPlan = this.planService.selectedPlanSignal();
							// 			if (refreshedPlan) {
							// 				if (refreshedPlan.purchaseLock === false) {
							// 					this.log.debug('Plan refreshed and confirmed unlocked', {
							// 						planId: refreshedPlan.id,
							// 						purchaseLock: refreshedPlan.purchaseLock,
							// 						planIsLocked: this.planService.planIsLockedSignal()
							// 					});
							// 				} else {
							// 					this.log.warn('Plan was locked again after refresh, re-unlocking', {
							// 						planId: refreshedPlan.id,
							// 						purchaseLock: refreshedPlan.purchaseLock
							// 					});
							// 					// Re-unlock the plan if it got locked again (backend might not have saved yet)
							// 					refreshedPlan.purchaseLock = false;
							// 					this.planService.selectedPlanSignal.set(refreshedPlan);
							// 					this.planService.setPlanIsLocked(false);
							// 				}
							// 			}
							// 		} catch (refreshError) {
							// 			this.log.error('Error refreshing plan after unlock:', refreshError);
							// 		}
							// 	})();
							// }, 1000);

							this.log.debug('Order number stuff');

							// Extract receipt data before showing success
							// Always set receipt data, even if extraction fails
							const orderNumber: string | null = verificationResult.orderNumber || paymentIntentId || null;
							try {
								await this.extractReceiptData(result, elements, orderNumber);
							} catch (receiptError) {
								this.log.error('Error extracting receipt data, using fallback:', receiptError);
								this.ensureReceiptData(orderNumber);
							}

							// Ensure receipt data is set (fallback if extraction didn't set it)
							this.ensureReceiptData(orderNumber);

							// Set success state - this will show the success message in the modal
							this.paymentSucceeded.set(true);
							void this.stripeCheckoutService.handlePaymentSuccess(result);

							// Emit receipt data to show receipt page
							const receipt = this.receiptData();
							if (receipt) {
								// Emit checkoutSucceeded to show receipt page
								this.checkoutSucceeded.emit(receipt);
								// Keep loading state until navigation completes
								// Use setTimeout to allow modal to navigate before clearing loading state
								setTimeout(() => {
									this.isProcessingPayment.set(false);
								}, 300);
								// Don't emit checkoutCompleted here - receipt page will handle closing
								return; // Exit early to skip finally block
							} else {
								// Fallback: if receipt data isn't available, still emit with basic data
								this.log.warn('Receipt data not available, using fallback');
								this.ensureReceiptData(orderNumber);
								const fallbackReceipt = this.receiptData();
								if (fallbackReceipt) {
									this.checkoutSucceeded.emit(fallbackReceipt);
									// Keep loading state until navigation completes
									setTimeout(() => {
										this.isProcessingPayment.set(false);
									}, 300);
									return; // Exit early to skip finally block
								}
							}
							// Don't emit checkoutCompleted - checkoutSucceeded handles showing receipt
							// this.checkoutCompleted.emit(successResult);
						} else {
							this.log.warn('Plan returned from verification still has purchaseLock = true', {
								planId: unlockedPlan.id,
								purchaseLock: unlockedPlan.purchaseLock
							});
							// Still emit success even if unlock failed - webhook will handle it
							void this.stripeCheckoutService.handlePaymentSuccess(result);
							// this.checkoutCompleted.emit(successResult);
						}
					} else {
						this.log.warn('Payment verification returned unsuccessful', {
							// success: verificationResult.success,
							planId: plan.id
						});
						// Still emit success - payment went through, verification might be delayed
						void this.stripeCheckoutService.handlePaymentSuccess(result);
						// this.checkoutCompleted.emit(successResult);
					}
				} catch (unlockError) {
					// Log error but don't fail the payment - webhook will handle it
					this.log.error('Error unlocking plan after payment:', unlockError);
					// Still emit success - payment went through
					void this.stripeCheckoutService.handlePaymentSuccess(result);
					// this.checkoutCompleted.emit(successResult);
				}
			} else {
				this.log.warn('Could not extract payment intent ID from result or client secret', {
					hasResult: !!result,
					resultType: typeof result
				});
				// Still emit success - payment might have succeeded
				void this.stripeCheckoutService.handlePaymentSuccess(result);
				// this.checkoutCompleted.emit(successResult);
			}
		} catch (error: unknown) {
			this.log.error('Error in handleCheckout:', error);
			const errorMsg = this.stripeCheckoutService.handlePaymentError(error);
			this.errorMessage.set(errorMsg);
			this.paymentErrorMessage.set(errorMsg);
			this.checkoutError.emit(error instanceof Error ? error : new Error(errorMsg));
			// Only set processing to false on error (success case handles it after navigation)
			this.isProcessingPayment.set(false);
		}
	}

	handleBack(): void {
		this.goBack.emit();
	}

	/**
	 * Ensures receipt data is set with at least basic information
	 */
	private ensureReceiptData(orderNumber?: string | null): void {
		// If receipt data is already set, only update orderNumber if it's missing
		const currentReceipt = this.receiptData();
		if (currentReceipt) {
			if (orderNumber && !currentReceipt.orderNumber) {
				this.receiptData.set({
					...currentReceipt,
					orderNumber
				});
			}
			return;
		}

		// Set basic receipt data
		this.receiptData.set({
			orderNumber: orderNumber || null,
			name: this.fullName() || '',
			billingAddress: {},
			cardLast4: undefined,
			subtotal: this.subtotal(),
			tax: this.tax(),
			total: this.total(),
			currency: this.currency().toUpperCase()
		});
	}

	/**
	 * Extracts receipt data from payment result and billing address
	 */
	private async extractReceiptData(
		result: unknown,
		elements: StripeElements,
		orderNumber?: string | null
	): Promise<void> {
		try {
			// Get billing address from Address Element
			const addressElement = elements.getElement('address');
			let billingAddress: ReceiptData['billingAddress'] = {};
			let cardLast4: string | undefined;

			if (addressElement) {
				const addressValue = await addressElement.getValue();
				if (addressValue.complete && addressValue.value.address) {
					billingAddress = {
						line1: addressValue.value.address.line1,
						line2: addressValue.value.address.line2,
						city: addressValue.value.address.city,
						state: addressValue.value.address.state,
						postal_code: addressValue.value.address.postal_code,
						country: addressValue.value.address.country
					};
				}
			}

			// Extract payment method details from result
			if (result && typeof result === 'object') {
				const resultObj = result as unknown as Record<string, unknown>;
				const paymentIntent = resultObj['paymentIntent'] as Record<string, unknown> | undefined;

				if (paymentIntent) {
					const paymentMethod = paymentIntent['payment_method'] as Record<string, unknown> | undefined;
					if (paymentMethod) {
						const card = paymentMethod['card'] as Record<string, unknown> | undefined;
						if (card && typeof card['last4'] === 'string') {
							cardLast4 = card['last4'];
						}
					}
				}

				// Also try direct access if paymentIntent is the result itself
				if (!cardLast4 && resultObj['payment_method']) {
					const paymentMethod = resultObj['payment_method'] as Record<string, unknown>;
					const card = paymentMethod['card'] as Record<string, unknown> | undefined;
					if (card && typeof card['last4'] === 'string') {
						cardLast4 = card['last4'];
					}
				}
			}

			// Get name from user data
			const name = this.fullName() || '';

			// Calculate tax based on billing address
			const calculatedTax = await this.taxService.calculateTax(this.subtotal(), {
				state: billingAddress.state,
				postal_code: billingAddress.postal_code,
				country: billingAddress.country
			});
			const calculatedTotal = this.subtotal() + calculatedTax;

			// Store receipt data with tax calculated from billing address
			this.receiptData.set({
				orderNumber: orderNumber || null,
				name,
				billingAddress,
				cardLast4,
				subtotal: this.subtotal(),
				tax: calculatedTax,
				total: calculatedTotal,
				currency: this.currency().toUpperCase()
			});

			this.log.debug('Receipt data extracted', {
				hasName: !!name,
				hasBillingAddress: Object.keys(billingAddress).length > 0,
				hasCardLast4: !!cardLast4
			});
		} catch (error) {
			this.log.error('Error extracting receipt data:', error);
			// Still create receipt with available data
			// Use current billing address for tax calculation
			const address = this.billingAddress();
			const calculatedTax = await this.taxService.calculateTax(this.subtotal(), address);
			const calculatedTotal = this.subtotal() + calculatedTax;

			this.receiptData.set({
				orderNumber: orderNumber || null,
				name: this.fullName() || '',
				billingAddress: {},
				cardLast4: undefined,
				subtotal: this.subtotal(),
				tax: calculatedTax,
				total: calculatedTotal,
				currency: this.currency().toUpperCase()
			});
		}
	}

	/**
	 * Handles the "Done" button click after successful payment
	 */
	onDone(): void {
		this.checkoutCompleted.emit({ done: true });
	}
}
